// 🔴 « Visite Rénovation » — ENTRÉE V1 (P-MARIO-03) de l'application terrain.
//
// 🔑 MODULE D'ENTRÉE, hors de `pages/chantier/` : il ORCHESTRE des routes d'autres
//    domaines (clients, lieux, dossiers) ; le relevé (`pages/chantier/`), lui, reste
//    strictement sur `/api/chantier/` (garde_frontiere_artisan).
//
//   utilisateur OmniHub AUTHENTIFIÉ → « Nouvelle visite » | « Reprendre une visite »
//   Nouvelle visite : client + adresse (dictés ou saisis) → clients proposés → choix
//   → lieux proposés → choix (ou nouveau lieu) → dossier (existant ou nouveau)
//   → CONFIRMATION explicite → dossier/lieu créés si besoin → relevé ouvert.
//
// ⛔ AUCUN SMS, ⛔ aucun lien à coller, ⛔ aucun code, ⛔ aucun numéro de dossier demandé.
// ⛔ AUCUNE écriture avant « Confirmer » : tout ce qui précède est LECTURE.
// 🔑 Capacités DÉCLARÉES par le tenant (⛔ aucun nom de tenant) : `profil_chantier`
//    (quel relevé ouvrir) et `dossier_activite` (quel dossier créer).
// 🔑 Reprise : relevés non clos de L'UTILISATEUR de la session (serveur), ⛔ un stockage local.
// ⚠️ Nom produit INVARIANT : « Visite Rénovation ».
import { useCallback, useEffect, useRef, useState } from "react";
import { Button } from "../../components/ui/button";
import { Encart } from "../chantier/AnnexesReleve";
import { ReleveChantier } from "../chantier/ReleveChantier";
import { CreerDevisReleve } from "../metier/artisan/CreerDevisReleve";
import { creerDevisDepuisReleve, estGenerationDevisEnCours, pdfDevis } from "../metier/artisan/api";
import { useDictee } from "../chantier/useDictee";
import { ajouterAdresseAuDossier, modifierAdresseDuDossier, siteDuDossier, capacitesDeLaSession, chercherClients, confirmer, decouperDictee, lieuDuDossier, lieuxDuClient, mesVisitesEnCours, plat, scoreLieu,
         ouvrirAccesSansLogin, rouvrirVisite, visitesDuDossier } from "./entreeVisite";
import { apiBureau } from "../chantier/api";
import { getDossierActivite, profilChantier } from "../../utils/tenantProfile";
import { useAuth } from "../../contexts/AuthContext";
import ConnexionTerrain from "../../components/auth/ConnexionTerrain";

const NOM = "Visite Rénovation";

/** 🔴 N°975 — sur le terrain, « Ouvrir le devis » montre SON PDF (route documentaire
 *  partagée, lue avec la session) : ⛔ pas de navigation vers l'ERP depuis le téléphone. */
async function ouvrirPdfDevis(devis) {
  const url = URL.createObjectURL(await pdfDevis(devis.devis_id));
  if (!window.open(url, "_blank")) window.location.assign(url);
}
/** 🔴 N°1028 — « Télécharger » : le MÊME PDF, enregistré (nom de fichier = numéro du devis). */
async function telechargerPdfDevis(devis) {
  const url = URL.createObjectURL(await pdfDevis(devis.devis_id));
  const a = document.createElement("a");
  a.href = url; a.download = `Devis_${String(devis.numero || devis.devis_id).replace(/[\/\\]/g, "-")}.pdf`;
  document.body.appendChild(a); a.click(); a.remove();
}
const MANIFEST = "/manifest-renovation.json";

const CARTE = { background: "var(--oh-bg-elevated)", border: "0.5px solid var(--oh-border)", borderRadius: 12,
                padding: 16, display: "grid", gap: 8, color: "var(--oh-text)" };
const CHAMP = { height: 44, borderRadius: 8, border: "0.5px solid var(--oh-border)", padding: "0 12px",
                background: "var(--oh-bg)", color: "var(--oh-text)", fontSize: 16 };
const PRIMAIRE = { background: "var(--oh-accent-solid)", color: "var(--oh-accent-contrast)", height: 44 };
const SECONDAIRE = { border: "0.5px solid var(--oh-accent)", color: "var(--oh-accent-text)", background: "transparent", height: 44 };
const DISCRET = { border: "0.5px solid var(--oh-border)", color: "var(--oh-text-secondary)", background: "transparent", height: 36 };

const adresseLieu = (s) => [s.nom_site, s.adresse, [s.code_postal, s.ville].filter(Boolean).join(" ")].filter(Boolean).join(" · ");
// ⚠️ `display_name` de la recherche = « société (prénom nom) » ⇒ « ABC (ABC) » : la raison
//    sociale d'abord pour une personne morale.
const nomClient = (c) => c.raison_sociale || c.display_name || [c.prenom, c.nom].filter(Boolean).join(" ");


/** Le manifest de CETTE application, le temps de l'écran : installée, elle rouvre ICI. */
function useManifest() {
  useEffect(() => {
    const lien = document.querySelector('link[rel="manifest"]');
    if (!lien) return undefined;
    const precedent = lien.getAttribute("href");
    lien.setAttribute("href", MANIFEST);
    return () => { if (precedent) lien.setAttribute("href", precedent); };
  }, []);
}

/** Capacités DÉCLARÉES (profil de relevé, activité du dossier). Cache d'OmniHub d'abord ;
 *  vide ⇒ lecture serveur — ⛔ afficher « aucun profil » sur un simple cache vide serait faux. */
//  🔑 Relu À CHAQUE ouverture de session : sur `visite.*`, OmniHub ⛔ tourne jamais et la
//     connexion ⛔ pose pas ces clés — sans relecture, l'écran dirait « aucun profil ».
function useCapacites(session) {
  const [etat, setEtat] = useState({ pret: false, profil: null, activite: null });
  useEffect(() => {
    if (!session) { setEtat({ pret: false, profil: null, activite: null }); return undefined; }
    const profil = profilChantier(), activite = getDossierActivite();
    if (profil && activite) { setEtat({ pret: true, profil, activite }); return undefined; }
    let vivant = true;
    capacitesDeLaSession()
      .then((c) => vivant && setEtat({ pret: true, ...c }))
      .catch(() => vivant && setEtat({ pret: true, profil: null, activite: null }));
    return () => { vivant = false; };
  }, [session]);
  return etat;
}

function Champ({ libelle, valeur, onChange, testId, placeholder }) {
  return (
    <label className="text-sm" style={{ display: "grid", gap: 4, color: "var(--oh-text)" }}>
      {libelle}
      <input data-testid={testId} value={valeur} placeholder={placeholder} autoComplete="off"
             onChange={(ev) => onChange(ev.target.value)} style={CHAMP} />
    </label>
  );
}

function Choix({ testId, titre, detail, onClick, actif }) {
  return (
    <button type="button" data-testid={testId} onClick={onClick} aria-pressed={Boolean(actif)}
            style={{ textAlign: "left", borderRadius: 8, padding: 12, cursor: "pointer",
                     border: `0.5px solid ${actif ? "var(--oh-accent)" : "var(--oh-border)"}`,
                     background: actif ? "var(--oh-accent-bg)" : "var(--oh-bg)", color: "var(--oh-text)" }}>
      <div style={{ fontWeight: 500 }}>{titre}</div>
      {detail && <div className="text-sm" style={{ color: "var(--oh-text-secondary)" }}>{detail}</div>}
    </button>
  );
}

function Accueil({ onNouvelle, onReprendre }) {
  const [enCours, setEnCours] = useState(null);
  const [erreur, setErreur] = useState("");
  useEffect(() => {
    let vivant = true;
    mesVisitesEnCours().then(async (liste) => {
      if (vivant) setEnCours(liste);
      // 🔴 P1 — l'adresse affichée est le SITE du dossier (lu une fois par dossier), ⛔ le libellé figé à l'ouverture de la visite.
      const lus = {};
      await Promise.all([...new Set(liste.map((r) => r.dossier_id).filter(Boolean))].map(async (d) => { lus[d] = await lieuDuDossier(d); }));
      if (vivant) setEnCours(liste.map((r) => (lus[r.dossier_id] === null || lus[r.dossier_id] === undefined ? r
        : { ...r, contexte: { ...(r.contexte || {}), lieu: lus[r.dossier_id] } })));
    }).catch((err) => { if (vivant) { setEnCours([]); setErreur(err.message); } });
    return () => { vivant = false; };
  }, []);
  return (
    <div style={{ display: "grid", gap: 16 }}>
      <Button data-testid="entree-nouvelle-visite" onClick={onNouvelle} style={PRIMAIRE}>Nouvelle visite</Button>
      {erreur && <Encart ton="error" testId="reprise-erreur">{erreur}</Encart>}
      {enCours === null && <p className="text-sm" style={{ color: "var(--oh-text-muted)" }}>Recherche des visites en cours…</p>}
      {enCours?.length > 0 && (
        <section data-testid="reprendre-visites" style={CARTE}>
          <div style={{ fontWeight: 500 }}>Reprendre une visite</div>
          {enCours.map((r) => (
            <Choix key={r.id} testId={`reprendre-${r.id}`} onClick={() => onReprendre(r)}
                   titre={r.contexte?.client || r.contexte?.dossier || "Visite en cours"}
                   detail={[r.contexte?.lieu, r.ouvert_le && `ouverte le ${new Date(r.ouvert_le).toLocaleString("fr-FR")}`,
                            `${r.nombre_postes || 0} ligne(s)`].filter(Boolean).join(" · ")} />
          ))}
        </section>
      )}
    </div>
  );
}

const quand = (t) => (t ? new Date(t).toLocaleString("fr-FR") : "");

/** 🔴 N°1016 — Les VISITES d'un dossier : en cours (⇒ Reprendre), closes (⇒ Consulter), et
 *  une close SANS devis (⇒ Rouvrir pour compléter : rien n'est perdu). ⛔ Aucun nom de client,
 *  de lieu ou de tenant ici : la liste vient du serveur, pour N'IMPORTE QUEL dossier. */
function VisitesDuDossier({ dossier, libelles, onOuvrir, onCharge }) {
  const [donnees, setDonnees] = useState(null);
  const [occupe, setOccupe] = useState("");
  const [erreur, setErreur] = useState("");
  const charger = useCallback(() => visitesDuDossier(dossier.id)
    .then((d) => { setDonnees(d); onCharge && onCharge(d); })
    .catch(() => { const vide = { visites: [], indisponible: true }; setDonnees(vide); onCharge && onCharge(vide); }), [dossier.id]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { charger(); }, [charger]);
  const ouvrir = () => onOuvrir({ dossierId: dossier.id, reference: dossier.reference,
                                  client: libelles.client, lieu: libelles.lieu });
  const rouvrir = async (v) => {
    setErreur(""); setOccupe(v.id);
    try { await rouvrirVisite(v.id); ouvrir(); }
    catch (err) { setErreur(err.message); } finally { setOccupe(""); }
  };
  if (!donnees) return <p className="text-sm" style={{ color: "var(--oh-text-muted)" }}>Recherche des visites…</p>;
  if (donnees.indisponible) return null;
  if (donnees.visites.length === 0) {
    return <p className="text-sm" data-testid={`aucune-visite-${dossier.id}`} style={{ color: "var(--oh-text-muted)" }}>Aucune visite pour ce dossier.</p>;
  }
  return (
    <div data-testid={`visites-${dossier.id}`} style={{ display: "grid", gap: 8 }}>
      {donnees.visites.map((v) => (
        <div key={v.id} data-testid={`visite-${v.id}`} style={{ display: "grid", gap: 4, padding: 8, borderRadius: 8,
                                                                  border: "0.5px solid var(--oh-border)" }}>
          <div className="text-sm" style={{ color: "var(--oh-text)" }}>
            {v.etat === "en_cours" ? "Visite en cours" : v.etat === "clos" ? "Visite terminée" : "Visite abandonnée"}
            {" · "}{v.etat === "en_cours" ? `ouverte le ${quand(v.ouvert_le)}` : `le ${quand(v.clos_le || v.ouvert_le)}`}
            {" · "}{v.nombre_postes || 0} ligne(s){v.devis_numero ? ` · devis ${v.devis_numero}` : ""}
          </div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {v.etat === "en_cours" && (
              <Button data-testid={`visite-reprendre-${v.id}`} onClick={ouvrir} style={PRIMAIRE}>Reprendre</Button>
            )}
            {v.etat !== "en_cours" && (
              <Button variant="outline" data-testid={`visite-consulter-${v.id}`} onClick={ouvrir} style={SECONDAIRE}>Consulter</Button>
            )}
            {v.rouvrable && (
              <Button variant="outline" data-testid={`visite-rouvrir-${v.id}`} disabled={occupe === v.id}
                      onClick={() => rouvrir(v)} style={PRIMAIRE}>
                {occupe === v.id ? "Réouverture…" : "Rouvrir pour compléter"}
              </Button>
            )}
          </div>
          {v.rouvrable && (
            <div className="text-sm" style={{ color: "var(--oh-text-secondary)" }}>
              Aucun devis n'a été créé pour cette visite : vous pouvez la rouvrir, les lignes saisies sont conservées.
            </div>
          )}
        </div>
      ))}
      {erreur && <Encart ton="error" testId="rouvrir-erreur">{erreur}</Encart>}
    </div>
  );
}

/** Le parcours validé : CLIENT → DOSSIERS du client → SITE (adresse) de chaque dossier → VISITES
 *  de ce site. Tout est visible d'un coup : ⛔ aucun dossier, aucun lieu retenu à la place de
 *  l'humain. ⛔ Rien ici ne connaît un client, une adresse ou un tenant : tout vient du serveur. */
function DossiersDesLieux({ lieux, client, onOuvrir }) {
  const dossiers = lieux.flatMap((s) => (s.dossiers || []).map((d) => ({ d, s })))
    .sort((x, y) => String(x.d.reference || "").localeCompare(String(y.d.reference || "")));
  const sansDossier = lieux.filter((s) => (s.dossiers || []).length === 0);
  return (
    <div data-testid="dossiers-des-lieux" style={{ display: "grid", gap: 12 }}>
      {dossiers.map(({ d, s }) => (
        <div key={d.id} data-testid={`dossier-visites-${d.id}`}
             style={{ display: "grid", gap: 6, padding: 12, borderRadius: 8, border: "0.5px solid var(--oh-border)" }}>
          <div style={{ fontWeight: 500 }}>Dossier {d.reference || "sans référence"}{d.statut ? ` · ${d.statut}` : ""}</div>
          <div data-testid={`lieu-visites-${s.id}`} className="text-sm" style={{ color: "var(--oh-text-secondary)" }}>
            Adresse : {adresseLieu(s)}
          </div>
          <VisitesDuDossier dossier={d} libelles={{ client, lieu: adresseLieu(s) }} onOuvrir={onOuvrir} />
        </div>
      ))}
      {sansDossier.map((s) => (
        <p key={s.id} data-testid={`lieu-sans-dossier-${s.id}`} className="text-sm" style={{ color: "var(--oh-text-muted)" }}>
          {adresseLieu(s)} — aucun dossier sur ce lieu.
        </p>
      ))}
    </div>
  );
}

/** « Retour aux visites » — le dossier de la visite quittée : ses visites, closes comprises. */
function VueDossier({ visite, profil, onOuvrir, onClient }) {
  const [charge, setCharge] = useState(null);
  const [erreur, setErreur] = useState("");
  const [occupe, setOccupe] = useState(false);
  const dossier = { id: visite.dossierId, reference: visite.reference };
  const libelles = { client: visite.client, lieu: visite.lieu };
  const enCours = (charge?.visites || []).some((v) => v.etat === "en_cours");
  const nouvelle = async () => {
    setErreur(""); setOccupe(true);
    try {
      await apiBureau(visite.dossierId).ouvrir(profil, { client: visite.client, lieu: visite.lieu, dossier: visite.reference || null });
      onOuvrir({ ...visite });
    } catch (err) { setErreur(err.message); } finally { setOccupe(false); }
  };
  return (
    <section data-testid="vue-dossier" style={CARTE}>
      <div style={{ fontWeight: 500 }}>{visite.client}</div>
      {/* La référence et l'adresse viennent du SERVEUR (dossier relu) : UN SEUL site, celui du dossier ; le contexte de reprise n'est qu'un libellé de secours tant que le serveur n'a pas répondu. */}
      {(charge?.dossier?.lieu || visite.lieu) && (
        <div className="text-sm" data-testid="vue-dossier-lieu" style={{ color: "var(--oh-text-secondary)" }}>{charge?.dossier?.lieu || visite.lieu}</div>
      )}
      <div className="text-sm" data-testid="vue-dossier-reference" style={{ color: "var(--oh-text-muted)" }}>
        Dossier {charge?.dossier?.reference || visite.reference || ""}
      </div>
      <VisitesDuDossier dossier={dossier} libelles={libelles} onOuvrir={onOuvrir} onCharge={setCharge} />
      {charge && !enCours && (
        <Button data-testid="dossier-nouvelle-visite" onClick={nouvelle} disabled={occupe} style={SECONDAIRE}>
          {occupe ? "Ouverture…" : "Nouvelle visite sur ce dossier"}
        </Button>
      )}
      {erreur && <Encart ton="error" testId="dossier-erreur">{erreur}</Encart>}
      <Button variant="outline" data-testid="voir-dossiers-client" onClick={onClient} style={DISCRET}>
        Tous les dossiers de ce client
      </Button>
    </section>
  );
}

/** « Retour au dossier » — le client, TOUS ses lieux et dossiers, chacun avec ses visites. */
function VueClient({ visite, onOuvrir }) {
  const [etat, setEtat] = useState({ lieux: null, client: null, erreur: "" });
  useEffect(() => {
    let vivant = true;
    (async () => {
      try {
        const { dossier } = await visitesDuDossier(visite.dossierId);
        const lieux = await lieuxDuClient(dossier.client_id, "");
        if (vivant) setEtat({ lieux, client: visite.client, erreur: "" });
      } catch (err) { if (vivant) setEtat({ lieux: [], client: null, erreur: err.message }); }
    })();
    return () => { vivant = false; };
  }, [visite.dossierId, visite.client]);
  return (
    <section data-testid="vue-client" style={CARTE}>
      <div style={{ fontWeight: 500 }}>{visite.client}</div>
      {etat.lieux === null && <p className="text-sm" style={{ color: "var(--oh-text-muted)" }}>Chargement des dossiers…</p>}
      {etat.erreur && <Encart ton="error" testId="client-erreur">{etat.erreur}</Encart>}
      {etat.lieux && <DossiersDesLieux lieux={etat.lieux} client={visite.client} onOuvrir={onOuvrir} />}
    </section>
  );
}

/** 🔴 N°1024 — une information VALIDÉE devient un bloc COMPACT (libellé, valeur, [Modifier]) :
 *  sur téléphone on ne voit que ce qui vient d'être validé et l'étape à faire, ⛔ pas l'historique du formulaire. */
function Valide({ testId, libelle, valeur, onModifier, testIdModifier }) {
  return (
    <div data-testid={testId} style={{ ...CARTE, padding: 12, gridTemplateColumns: "1fr auto", alignItems: "center" }}>
      <div>
        <div className="text-sm" style={{ color: "var(--oh-text-muted)" }}>{libelle}</div>
        <div style={{ fontWeight: 500 }}>{valeur}</div>
      </div>
      {onModifier && (
        <Button variant="outline" data-testid={testIdModifier} onClick={onModifier} style={{ ...DISCRET, height: 32 }}>Modifier</Button>
      )}
    </div>
  );
}

/** L'ACTION sur la visite, une fois client + adresse + dossier connus : « Visite en cours — Reprendre »
 *  s'il y en a une, sinon « Commencer la visite ». L'historique reste là, REPLIÉ. */
function ActionVisite({ dossier, client, lieu, onOuvrir, onCommencer, occupe }) {
  const [donnees, setDonnees] = useState(null);
  const libelles = { client: nomClient(client), lieu: adresseLieu(lieu) };
  const enCours = (donnees?.visites || []).find((v) => v.etat === "en_cours");
  const ouvrir = () => onOuvrir({ dossierId: dossier.id, reference: dossier.reference, client: libelles.client, lieu: libelles.lieu });
  return (
    <section data-testid="action-visite" style={CARTE}>
      <div className="text-sm" data-testid="recap-dossier" style={{ color: "var(--oh-text-muted)" }}>
        Dossier {dossier.reference || "sans référence"}
      </div>
      {!donnees && <p className="text-sm" style={{ color: "var(--oh-text-muted)" }}>Recherche des visites…</p>}
      {donnees && enCours && (
        <>
          <div style={{ fontWeight: 500 }} data-testid="visite-en-cours-titre">Visite en cours</div>
          <div className="text-sm" style={{ color: "var(--oh-text-secondary)" }}>
            ouverte le {quand(enCours.ouvert_le)} · {enCours.nombre_postes || 0} ligne(s)
          </div>
          <Button data-testid="reprendre-visite-en-cours" onClick={ouvrir} style={PRIMAIRE}>Reprendre</Button>
        </>
      )}
      {donnees && !enCours && (
        <Button data-testid="confirmer-visite" onClick={onCommencer} disabled={occupe} style={PRIMAIRE}>
          {occupe ? "Ouverture…" : "Commencer la visite"}
        </Button>
      )}
      <details data-testid="historique-visites">
        <summary className="text-sm" style={{ color: "var(--oh-text-secondary)", cursor: "pointer" }}>Historique des visites de ce dossier</summary>
        <div data-testid="visites-existantes" style={{ marginTop: 8 }}>
          <VisitesDuDossier dossier={dossier} libelles={libelles} onOuvrir={onOuvrir} onCharge={setDonnees} />
        </div>
      </details>
    </section>
  );
}

function NouvelleVisite({ onAnnuler, onConfirme, onOuvrir, profil, activite }) {
  const [saisie, setSaisie] = useState({ client: "", lieu: "" });
  const [clients, setClients] = useState(null);
  const [client, setClient] = useState(null);
  const [lieux, setLieux] = useState(null);
  const [lieu, setLieu] = useState(null);               // { ...site } | { nouveau: {...} }
  const [nouveauLieu, setNouveauLieu] = useState(null);  // formulaire, avant choix
  const [dossier, setDossier] = useState(undefined);     // undefined = à choisir · null = nouveau · objet = dossier
  const [adresseCherchee, setAdresseCherchee] = useState(false);
  const [voirToutes, setVoirToutes] = useState(false);
  const [erreur, setErreur] = useState("");
  const [occupe, setOccupe] = useState(false);

  // 🔴 N°1023 — UNE dictée, deux CIBLES : « client » lance la recherche dès la transcription ;
  //    « adresse » remplit l'adresse et la fait RECONNAÎTRE (⛔ ne touche pas au client).
  const cibleDictee = useRef("client");
  const rechercherRef = useRef(null);
  const dicte = useCallback((t) => {
    if (cibleDictee.current === "adresse") {
      setSaisie((x) => ({ ...x, lieu: String(t || "").trim() })); setAdresseCherchee(true); return;
    }
    const d = decouperDictee(t);
    setSaisie(d);
    if (d.client.trim().length >= 2 && rechercherRef.current) rechercherRef.current(d.client);
  }, []);
  const { disponible: micro, ecoute, erreur: erreurMicro, demarrer } = useDictee(dicte);
  const dicter = (cible) => { cibleDictee.current = cible; demarrer(); };

  const garder = async (f) => { setErreur(""); setOccupe(true); try { await f(); } catch (err) { setErreur(err.message); } finally { setOccupe(false); } };
  const rechercher = (texte) => garder(async () => {
    setClient(null); setLieux(null); setLieu(null); setDossier(undefined);
    setClients(await chercherClients(typeof texte === "string" ? texte : saisie.client));
  });
  rechercherRef.current = rechercher;
  const choisirClient = (c) => garder(async () => {
    setClient(c); setLieu(null); setDossier(undefined); setNouveauLieu(null); setVoirToutes(false);
    setLieux(await lieuxDuClient(c.id, saisie.lieu));
    setAdresseCherchee(Boolean(saisie.lieu.trim()));            // adresse déjà dictée avec le client ⇒ reconnue d'emblée
  });
  const modifierClient = () => {
    setClient(null); setLieux(null); setLieu(null); setDossier(undefined); setNouveauLieu(null);
    setAdresseCherchee(false); setVoirToutes(false);
  };
  const modifierAdresse = () => { setLieu(null); setDossier(undefined); setNouveauLieu(null); };
  // 🔴 N°1024 — CLIENT → ADRESSE → VISITE. Le dossier se déduit de l'adresse : UN dossier ⇒ retenu sans question ;
  //    AUCUN ⇒ nouveau dossier à l'ouverture ; PLUSIEURS pour la même adresse ⇒ seulement alors, Mario choisit.
  const choisirLieu = (s) => {
    setLieu(s); setNouveauLieu(null);
    const ds = s.dossiers || [];
    setDossier(ds.length === 1 ? ds[0] : ds.length === 0 ? null : undefined);
  };
  const valider = () => garder(async () => {
    const libLieu = lieu.nouveau ? adresseLieu(lieu.nouveau) : adresseLieu(lieu);
    const r = await confirmer({ client, lieu, dossier, activite, profilCode: profil,
                                libelles: { client: nomClient(client), lieu: libLieu } });
    onConfirme({ dossierId: r.dossierId, client: nomClient(client), lieu: libLieu, reference: r.reference });
  });

  // L'adresse dictée est rapprochée des adresses de CE client (même fonction `scoreLieu` qu'avant).
  const adresses = (lieux || []).map((s) => ({ ...s, score: scoreLieu(s, saisie.lieu) }));
  // « Reconnue » = elle partage au moins 2 mots avec l'adresse dictée (1 seul mot commun, « rue » par exemple,
  // ne désigne pas une adresse) ET elle est la mieux placée ; à égalité ⇒ plusieurs ⇒ Mario choisit.
  const motsSaisis = plat(saisie.lieu).split(/\s+/).filter((m) => m.length >= 2).length;
  const seuilAdresse = Math.min(2, Math.max(1, motsSaisis));
  const meilleurScore = Math.max(0, ...adresses.map((s) => s.score));
  const reconnues = adresses.filter((s) => s.score >= seuilAdresse && s.score === meilleurScore);
  const pret = client && lieu && dossier !== undefined;
  const dossiersDeLAdresse = lieu && !lieu.nouveau ? (lieu.dossiers || []) : [];

  return (
    <div style={{ display: "grid", gap: 16 }}>
      {/* ── ÉTAPE CLIENT (disparaît dès que le client est validé) ───────────────────────────── */}
      {!client && (
        <section style={CARTE} data-testid="saisie-visite">
          <div style={{ fontWeight: 500 }}>Nouvelle visite — le client</div>
          {micro && (
            <Button variant="outline" data-testid="dicter-client" onClick={() => dicter("client")} disabled={ecoute} style={SECONDAIRE}>
              {ecoute ? "J'écoute…" : "Dicter le client"}
            </Button>
          )}
          {erreurMicro && <Encart ton="error" testId="dictee-erreur">{erreurMicro}</Encart>}
          <Champ libelle="Client" testId="saisie-client" valeur={saisie.client} placeholder="ex. Syndic ABC"
                 onChange={(v) => setSaisie((x) => ({ ...x, client: v }))} />
          <Button data-testid="rechercher-client" onClick={() => rechercher()} disabled={occupe || saisie.client.trim().length < 2}
                  style={PRIMAIRE}>Rechercher</Button>
        </section>
      )}

      {!client && clients && (
        <section style={CARTE} data-testid="clients-proposes">
          <div style={{ fontWeight: 500 }}>
            {clients.length === 0 ? "Client" : clients.length === 1 ? "Client trouvé — validez-le" : "Plusieurs clients possibles — choisissez"}
          </div>
          {clients.length === 0 && (
            <Encart ton="info" testId="aucun-client">Aucun client ne correspond. Vérifiez la saisie, ou créez le client depuis OmniHub.</Encart>
          )}
          {clients.map((c) => (
            <Choix key={c.id} testId={`client-${c.id}`} onClick={() => choisirClient(c)}
                   titre={nomClient(c)}
                   detail={[c.rapprochement === "proche" ? "Correspondance approximative de la dictée — vérifiez" : null,
                            c.telephone, c.email].filter(Boolean).join(" · ")} />
          ))}
          {clients.length === 1 && (
            <Button data-testid="valider-client" onClick={() => choisirClient(clients[0])} disabled={occupe} style={PRIMAIRE}>
              Valider : {nomClient(clients[0])}
            </Button>
          )}
        </section>
      )}

      {client && (
        <Valide testId="recap-client" libelle="Client" valeur={nomClient(client)} onModifier={modifierClient}
                testIdModifier="modifier-client" />
      )}

      {/* ── ÉTAPE ADRESSE (disparaît dès que l'adresse est validée) ───────────────────────────── */}
      {client && !lieu && (
        <section style={CARTE} data-testid="etape-adresse">
          <div style={{ fontWeight: 500 }}>Adresse</div>
          {micro && (
            <Button variant="outline" data-testid="dicter-adresse" onClick={() => dicter("adresse")} disabled={ecoute} style={SECONDAIRE}>
              {ecoute ? "J'écoute…" : "Dicter l'adresse"}
            </Button>
          )}
          <Champ libelle="Adresse ou lieu" testId="saisie-lieu" valeur={saisie.lieu} placeholder="ex. 12 rue X"
                 onChange={(v) => setSaisie((x) => ({ ...x, lieu: v }))} />
          <Button variant="outline" data-testid="chercher-adresse" style={SECONDAIRE} disabled={!saisie.lieu.trim()}
                  onClick={() => setAdresseCherchee(true)}>Rechercher l'adresse</Button>

          {adresseCherchee && saisie.lieu.trim() && reconnues.length > 0 && (
            <div data-testid="adresses-reconnues" style={{ display: "grid", gap: 8 }}>
              <div className="text-sm" style={{ color: "var(--oh-text-secondary)" }}>
                {reconnues.length === 1 ? "Adresse reconnue" : "Plusieurs adresses possibles — choisissez"}
              </div>
              {reconnues.map((s) => (
                <Choix key={s.id} testId={`lieu-${s.id}`} onClick={() => choisirLieu(s)} titre={adresseLieu(s)} />
              ))}
              {reconnues.length === 1 && (
                <Button data-testid="valider-adresse" onClick={() => choisirLieu(reconnues[0])} style={PRIMAIRE}>
                  Valider cette adresse
                </Button>
              )}
            </div>
          )}
          {adresseCherchee && saisie.lieu.trim() && reconnues.length === 0 && (
            <Encart ton="info" testId="adresse-non-trouvee">
              Cette adresse n'est pas trouvée pour ce client. Vous pouvez la créer ou voir ses autres adresses.
            </Encart>
          )}

          {voirToutes && adresses.map((s) => (
            <Choix key={`t-${s.id}`} testId={`lieu-${s.id}`} onClick={() => choisirLieu(s)} titre={adresseLieu(s)} />
          ))}
          {!nouveauLieu && (
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              {adresses.length > 0 && !voirToutes && (
                <Button variant="outline" data-testid="toutes-adresses" style={DISCRET} onClick={() => setVoirToutes(true)}>
                  Voir les adresses du client
                </Button>
              )}
              <Button variant="outline" data-testid="nouveau-lieu" style={DISCRET}
                      onClick={() => setNouveauLieu({ adresse: saisie.lieu, code_postal: "", ville: "" })}>
                {adresseCherchee && reconnues.length === 0 && saisie.lieu.trim() ? "Créer cette adresse" : "Nouvelle adresse"}
              </Button>
            </div>
          )}
          {nouveauLieu && (
            <div data-testid="formulaire-nouveau-lieu" style={{ display: "grid", gap: 8 }}>
              <Champ libelle="Adresse" testId="nouveau-lieu-adresse" valeur={nouveauLieu.adresse}
                     onChange={(v) => setNouveauLieu((n) => ({ ...n, adresse: v }))} />
              <Champ libelle="Code postal" testId="nouveau-lieu-cp" valeur={nouveauLieu.code_postal}
                     onChange={(v) => setNouveauLieu((n) => ({ ...n, code_postal: v }))} />
              <Champ libelle="Ville" testId="nouveau-lieu-ville" valeur={nouveauLieu.ville}
                     onChange={(v) => setNouveauLieu((n) => ({ ...n, ville: v }))} />
              <Button variant="outline" data-testid="nouveau-lieu-retenir" style={SECONDAIRE}
                      disabled={!nouveauLieu.adresse.trim() || !nouveauLieu.ville.trim()}
                      onClick={() => {
                        const n = { adresse: nouveauLieu.adresse.trim(), ville: nouveauLieu.ville.trim(),
                                    ...(nouveauLieu.code_postal.trim() ? { code_postal: nouveauLieu.code_postal.trim() } : {}) };
                        setLieu({ nouveau: n }); setDossier(null); setNouveauLieu(null);
                      }}>
                Retenir cette adresse (créée à l'ouverture de la visite)
              </Button>
            </div>
          )}
        </section>
      )}

      {client && lieu && (
        <Valide testId="recap-lieu" libelle="Adresse"
                valeur={lieu.nouveau ? `${adresseLieu(lieu.nouveau)} (nouvelle adresse)` : adresseLieu(lieu)}
                onModifier={modifierAdresse} testIdModifier="modifier-adresse" />
      )}

      {/* ── DOSSIER : seulement s'il y en a PLUSIEURS pour la même adresse ─────────────────────── */}
      {client && lieu && dossier === undefined && (
        <section style={CARTE} data-testid="choix-dossier">
          <div style={{ fontWeight: 500 }}>Plusieurs dossiers pour cette adresse — lequel ?</div>
          {dossiersDeLAdresse.map((d) => (
            <Choix key={d.id} testId={`dossier-${d.id}`} onClick={() => setDossier(d)}
                   titre={`Dossier ${d.reference || "sans référence"}`}
                   detail={[d.statut, d.created_at && `créé le ${new Date(d.created_at).toLocaleDateString("fr-FR")}`].filter(Boolean).join(" · ")} />
          ))}
          <Choix testId="dossier-nouveau" onClick={() => setDossier(null)} titre="Nouveau dossier pour cette adresse" />
        </section>
      )}

      {/* ── LA VISITE : Reprendre si une visite est en cours, sinon Commencer ───────────────────── */}
      {pret && dossier && (
        <ActionVisite dossier={dossier} client={client} lieu={lieu} onOuvrir={onOuvrir} onCommencer={valider} occupe={occupe} />
      )}
      {pret && !dossier && (
        <section style={CARTE} data-testid="action-visite">
          <div className="text-sm" data-testid="recap-dossier" style={{ color: "var(--oh-text-muted)" }}>
            {lieu.nouveau ? "Nouveau dossier et nouvelle adresse, créés à l'ouverture" : "Nouveau dossier sur cette adresse"}
          </div>
          <Button data-testid="confirmer-visite" onClick={valider} disabled={occupe} style={PRIMAIRE}>
            {occupe ? "Ouverture…" : "Commencer la visite"}
          </Button>
          {!lieu.nouveau && dossiersDeLAdresse.length > 0 && (
            <Button variant="outline" data-testid="dossier-existant" style={DISCRET}
                    onClick={() => setDossier(dossiersDeLAdresse.length === 1 ? dossiersDeLAdresse[0] : undefined)}>
              Utiliser un dossier existant
            </Button>
          )}
        </section>
      )}
      {pret && dossier && (
        <Button variant="outline" data-testid="dossier-nouveau" style={DISCRET} onClick={() => setDossier(null)}>
          Nouveau dossier pour cette adresse
        </Button>
      )}

      {erreur && <Encart ton="error" testId="entree-erreur">{erreur}</Encart>}
      <Button variant="outline" data-testid="annuler-nouvelle-visite" onClick={onAnnuler} style={DISCRET}>Annuler</Button>
    </div>
  );
}

/** 🔴 P1 (démo Laurent) — dossier SANS adresse : on propose de la CRÉER (site du client) et on continue sans quitter la visite. */
export function AdresseAbsente({ dossierId, onAjoutee, adresseExistante = false }) {
  const [ouvert, setOuvert] = useState(false);
  const [f, setF] = useState({ adresse: "", code_postal: "", ville: "" });
  const [occupe, setOccupe] = useState(false);
  const [erreur, setErreur] = useState("");
  const [fait, setFait] = useState("");
  const creer = async () => {
    setOccupe(true); setErreur("");
    try {
      const { site, rattache } = await ajouterAdresseAuDossier(dossierId, f, { rattacher: !adresseExistante });
      setOuvert(false); setF({ adresse: "", code_postal: "", ville: "" });                // le formulaire se referme : pas de ré-enregistrement par erreur
      if (rattache) onAjoutee(adresseLieu(site));
      else setFait(`Adresse ajoutée aux adresses du client : ${adresseLieu(site)}`);
    } catch (err) { setErreur(err.message); } finally { setOccupe(false); }
  };
  return (
    <div data-testid="adresse-absente" style={{ display: "grid", gap: 8 }}>
      {!adresseExistante && <Encart ton="info" testId="adresse-absente-message">Adresse du chantier absente.</Encart>}
      {fait && <Encart ton="success" testId="adresse-ajoutee-client">{fait}</Encart>}
      {!ouvert && <Button variant="outline" data-testid="ajouter-adresse-visite" style={adresseExistante ? DISCRET : SECONDAIRE} onClick={() => { setFait(""); setOuvert(true); }}>
        {adresseExistante ? "Ajouter une adresse" : "Ajouter l'adresse"}</Button>}
      {ouvert && (
        <div data-testid="formulaire-adresse-visite" style={{ display: "grid", gap: 8 }}>
          <Champ libelle="Adresse" testId="adresse-visite-adresse" valeur={f.adresse} onChange={(v) => setF((x) => ({ ...x, adresse: v }))} />
          <Champ libelle="Code postal" testId="adresse-visite-cp" valeur={f.code_postal} onChange={(v) => setF((x) => ({ ...x, code_postal: v }))} />
          <Champ libelle="Ville" testId="adresse-visite-ville" valeur={f.ville} onChange={(v) => setF((x) => ({ ...x, ville: v }))} />
          <Button data-testid="adresse-visite-creer" style={PRIMAIRE} disabled={occupe || !f.adresse.trim() || !f.ville.trim()} onClick={creer}>
            {occupe ? "Enregistrement…" : "Enregistrer l'adresse"}
          </Button>
        </div>
      )}
      {erreur && <Encart ton="error" testId="adresse-visite-erreur">{erreur}</Encart>}
    </div>
  );
}

/** 🔴 P1 (lot S) — [Modifier l'adresse] dans l'en-tête, sans quitter la visite : sauvegarde IMMÉDIATE (PATCH du site du dossier), l'adresse affichée se met à jour aussitôt.
 *  Le serveur refuse un doublon (même adresse chez ce client) et une modification qui réécrirait un devis émis : son message est affiché tel quel. Le rattachement du dossier ne change pas. */
export function AdresseModifier({ dossierId, onModifiee }) {
  const [ouvert, setOuvert] = useState(false);
  const [f, setF] = useState({ adresse: "", code_postal: "", ville: "" });
  const [occupe, setOccupe] = useState(false);
  const [erreur, setErreur] = useState("");
  const ouvrir = async () => {
    setErreur(""); setOuvert(true);
    try { const s = await siteDuDossier(dossierId); if (s) setF({ adresse: s.adresse || "", code_postal: s.code_postal || "", ville: s.ville || "" }); }
    catch (err) { setErreur(err.message); }
  };
  const enregistrer = async () => {
    setOccupe(true); setErreur("");
    try { const s = await modifierAdresseDuDossier(dossierId, f); setOuvert(false); onModifiee(adresseLieu(s)); }
    catch (err) { setErreur(err.message); } finally { setOccupe(false); }
  };
  return (
    <div data-testid="adresse-modifier" style={{ display: "grid", gap: 8 }}>
      {!ouvert && <Button variant="outline" data-testid="modifier-adresse-visite" style={DISCRET} onClick={ouvrir}>Modifier l'adresse</Button>}
      {ouvert && (
        <div data-testid="formulaire-modifier-adresse" style={{ display: "grid", gap: 8 }}>
          <Champ libelle="Adresse" testId="modifier-adresse-adresse" valeur={f.adresse} onChange={(v) => setF((x) => ({ ...x, adresse: v }))} />
          <Champ libelle="Code postal" testId="modifier-adresse-cp" valeur={f.code_postal} onChange={(v) => setF((x) => ({ ...x, code_postal: v }))} />
          <Champ libelle="Ville" testId="modifier-adresse-ville" valeur={f.ville} onChange={(v) => setF((x) => ({ ...x, ville: v }))} />
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <Button data-testid="modifier-adresse-enregistrer" style={PRIMAIRE} disabled={occupe || !f.adresse.trim() || !f.ville.trim()} onClick={enregistrer}>
              {occupe ? "Enregistrement…" : "Enregistrer l'adresse"}
            </Button>
            <Button variant="outline" data-testid="modifier-adresse-annuler" style={DISCRET} onClick={() => setOuvert(false)}>Annuler</Button>
          </div>
        </div>
      )}
      {erreur && <Encart ton="error" testId="modifier-adresse-erreur">{erreur}</Encart>}
    </div>
  );
}

function EnteteVisite({ visite, onQuitter, onVisites, onDossier, menuOuvert, onMenu, onAdresse }) {
  // 🔴 N°1025 — RÈGLE UX MOBILE : l'écran principal ne montre que l'action immédiate. L'entête = ☰ + client + adresse.
  //    Référence technique et navigation (Accueil / Retour aux visites / Retour au dossier) sont dans le menu ☰.
  return (
    <section data-testid="dossier-identifie" style={{ ...CARTE, gap: 8 }}>
      <div style={{ display: "grid", gridTemplateColumns: "auto 1fr", gap: 12, alignItems: "center" }}>
        <Button variant="outline" data-testid="menu-visite" aria-label="Menu" aria-expanded={Boolean(menuOuvert)} onClick={onMenu}
                style={{ ...DISCRET, height: 40, width: 40, padding: 0, fontSize: 20 }}>☰</Button>
        <div>
          <strong data-testid="visite-client" style={{ fontWeight: 500 }}>{visite.client || visite.reference || "Visite"}</strong>
          {visite.lieu && <div className="text-sm" data-testid="visite-lieu" style={{ color: "var(--oh-text-secondary)" }}>{visite.lieu}</div>}
        </div>
      </div>
      {visite.dossierId && onAdresse && <AdresseAbsente dossierId={visite.dossierId} onAjoutee={onAdresse} adresseExistante={Boolean(visite.lieu)} />}
      {visite.dossierId && onAdresse && visite.lieu && <AdresseModifier dossierId={visite.dossierId} onModifiee={onAdresse} />}
      {menuOuvert && (
        <div data-testid="menu-navigation" style={{ display: "grid", gap: 8 }}>
          {visite.reference && <div className="text-sm" data-testid="dossier-reference" style={{ color: "var(--oh-text-muted)" }}>{visite.reference}</div>}
          <Button variant="outline" data-testid="quitter-visite" onClick={onQuitter} style={{ ...DISCRET, height: 36 }}>Accueil Visite</Button>
          <Button variant="outline" data-testid="retour-visites" onClick={onVisites} style={{ ...DISCRET, height: 36 }}>Retour aux visites</Button>
          <Button variant="outline" data-testid="retour-dossier" onClick={onDossier} style={{ ...DISCRET, height: 36 }}>Retour au dossier</Button>
        </div>
      )}
    </section>
  );
}

export function VisiteRenovation() {
  useManifest();
  // 🔴 P-MARIO-20 — la SESSION vient de l'AUTHENTIFICATION OMNIHUB (AuthProvider), ⛔ d'un
  //    jeton lu en local. Le spinner ne couvre que le PREMIER contrôle : pendant la
  //    soumission, le formulaire reste monté (leçon N°498 de MobileVisitePage).
  const { isAuthenticated, loading: authEnCours, checkAuth } = useAuth();
  const [authVerifiee, setAuthVerifiee] = useState(false);
  const [accesOuvertTente, setAccesOuvertTente] = useState(false);
  // 🔴 N°1021-A — sans session, UNE tentative d'accès ouvert ; fermé ⇒ login actuel.
  useEffect(() => {
    if (authEnCours) return;
    if (isAuthenticated) { setAuthVerifiee(true); return; }
    if (accesOuvertTente) return;
    setAccesOuvertTente(true);
    ouvrirAccesSansLogin()
      .then((ok) => (ok && checkAuth ? checkAuth() : null))
      .finally(() => setAuthVerifiee(true));
  }, [authEnCours, isAuthenticated, accesOuvertTente, checkAuth]);
  const session = Boolean(isAuthenticated);
  const { profil, activite, pret } = useCapacites(session);
  const [etape, setEtape] = useState("accueil");
  const [visite, setVisite] = useState(null);
  // 🔴 P1 — l'adresse de la visite = le SITE du dossier, relu au serveur (une fois par dossier ouvert) ; ⛔ jamais le libellé figé à l'ouverture. Lecture impossible ⇒ on garde l'affichage.
  const dossierDeLaVisite = visite?.dossierId;
  useEffect(() => {
    if (!dossierDeLaVisite) return undefined;
    let vivant = true;
    lieuDuDossier(dossierDeLaVisite).then((lieu) => {
      if (vivant && lieu) setVisite((v) => (v && v.dossierId === dossierDeLaVisite && v.lieu !== lieu ? { ...v, lieu } : v));
    });
    return () => { vivant = false; };
  }, [dossierDeLaVisite]);
  const [menu, setMenu] = useState(false);          // 🔴 N°1025 — menu ☰ de la visite, fermé par défaut
  useEffect(() => { setMenu(false); }, [visite]);

  let contenu;
  if (!authVerifiee) {
    contenu = <p className="text-sm" style={{ color: "var(--oh-text-muted)" }}>Chargement…</p>;
  } else if (!session) {
    // 🔴 P-MARIO-20 — ⛔ plus de lien vers « / » : sur visite.omnihub-ia.fr, « / » est
    //    redirigé par nginx vers /mobile/chantier (ancien parcours SMS). On se connecte
    //    ICI, avec l'écran de connexion terrain EXISTANT (même autorité que l'ERP :
    //    `useAuth().loginWithEmail`) ; la session se pose sur CETTE origine et l'écran
    //    reste sur /mobile/renovation — ⛔ aucune redirection, donc aucun retour à perdre.
    contenu = <ConnexionTerrain titre="Visite Rénovation" />;
  } else if (!pret) {
    contenu = <p className="text-sm" style={{ color: "var(--oh-text-muted)" }}>Chargement…</p>;
  } else if (!profil || !activite) {
    contenu = (
      <Encart ton="info" testId="profil-absent">
        Aucun profil de relevé n'est déclaré pour cette entreprise : la visite ne peut pas être ouverte.
      </Encart>
    );
  } else if (etape === "releve" && visite) {
    contenu = (
      <>
        <EnteteVisite visite={visite} onQuitter={() => { setMenu(false); setVisite(null); setEtape("accueil"); }}
                      onVisites={() => { setMenu(false); setEtape("dossier"); }} onDossier={() => { setMenu(false); setEtape("client"); }}
                      menuOuvert={menu} onMenu={() => setMenu((m) => !m)} onAdresse={(lieu) => setVisite((v) => ({ ...v, lieu }))} />
        {/* 🔑 P-MARIO-15 — LE MÊME composant, avec les MÊMES props que la fiche dossier d'OmniHub
            (EmplacementVisites) : ⛔ un second relevé. */}
        <ReleveChantier compact menuOuvert={menu} dossierId={visite.dossierId} profilCode={profil}
                        actionsReleve={(r, ctx) => <CreerDevisReleve releve={r} onOuvrir={ouvrirPdfDevis} onTelecharger={telechargerPdfDevis} devisInitial={ctx?.devis || null} />}
                        finaliser={async (r) => {
                          // 🔴 N°1027 — « Créer le devis » = CLÔTURE puis DEVIS à partir des travaux enregistrés (pont existant, inchangé).
                          //    Devis refusé (ex. travail sans référence catalogue) ⇒ la visite est ROUVERTE : rien n'est perdu, on corrige.
                          await apiBureau(visite.dossierId).terminer(r.id);
                          try { return await creerDevisDepuisReleve(r.id); }
                          catch (err) {
                            // 🔴 12/10 — « génération en cours » (une autre demande crée CE devis) ⛔ n'est pas un refus :
                            //    on ⛔ ne rouvre PAS la visite. On redemande UNE fois : le serveur attend lui-même le devis de
                            //    l'autre demande et rend LE MÊME. Tout autre refus rouvre la visite, comme avant.
                            if (estGenerationDevisEnCours(err)) return await creerDevisDepuisReleve(r.id);
                            try { await rouvrirVisite(r.id); } catch { /* signalé par l'erreur d'origine */ }
                            throw err;
                          }
                        }} />
      </>
    );
  } else if ((etape === "dossier" || etape === "client") && visite) {
    const ouvrir = (v) => { setVisite(v); setEtape("releve"); };
    contenu = (
      <>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <Button variant="outline" data-testid="quitter-visite" onClick={() => { setVisite(null); setEtape("accueil"); }}
                  style={{ ...DISCRET, height: 32 }}>Accueil Visite</Button>
          <Button variant="outline" data-testid="retour-releve" onClick={() => setEtape("releve")}
                  style={{ ...DISCRET, height: 32 }}>Revenir à la visite</Button>
        </div>
        {etape === "dossier"
          ? <VueDossier visite={visite} profil={profil} onOuvrir={ouvrir} onClient={() => setEtape("client")} />
          : <VueClient visite={visite} onOuvrir={ouvrir} />}
      </>
    );
  } else if (etape === "nouvelle") {
    contenu = (
      <NouvelleVisite profil={profil} activite={activite} onAnnuler={() => setEtape("accueil")}
                      onOuvrir={(v) => { setVisite(v); setEtape("releve"); }}
                      onConfirme={(v) => { setVisite(v); setEtape("releve"); }} />
    );
  } else {
    contenu = (
      <Accueil onNouvelle={() => setEtape("nouvelle")}
               onReprendre={(r) => { setVisite({ dossierId: r.dossier_id, client: r.contexte?.client,
                                                 lieu: r.contexte?.lieu, reference: r.contexte?.dossier });
                                     setEtape("releve"); }} />
    );
  }

  return (
    <main data-testid="visite-renovation"
          style={{ minHeight: "100vh", background: "var(--oh-bg)", color: "var(--oh-text)", padding: 16 }}>
      <div style={{ maxWidth: 560, margin: "0 auto", display: "grid", gap: 16 }}>
        <h1 data-testid="visite-renovation-titre" style={{ fontSize: 20, fontWeight: 500, margin: 0 }}>{NOM}</h1>
        {contenu}
      </div>
    </main>
  );
}

export default VisiteRenovation;
